/* eslint-disable react/prop-types */
import React from 'react';
import { Row, Col, Select, Skeleton, Form } from 'antd';
import { Button, Modal } from 'components/sdk';
import { Input as GyramaisInput } from 'components/gyramais';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import './society.less';

/**
 * @typedef {{
 *  cnpj: string,
 *  legalName: string,
 *  capitalPercentage: string,
 *  lastYearRob: string,
 *  situation: 'Fiador/Avalista' | 'Interveniente' | 'Vinculado à Sociedade como Sócio',
 * }} SocietyData
 */

/**
 *
 * @param {{
 * index: number,
 * startingParams: SocietyData
 * }} param0
 */
const AddSocietyModal = ({
  society,
  onSubmit = () => {},

  visible = false,
  onClose,
}) => {
  const [form] = Form.useForm();

  const onComplete = async () => {
    await form.validateFields();
    const { cnpj, legalName, capitalPercentage, lastYearRob, situation } = form.getFieldsValue();

    onSubmit({
      cnpj,
      legalName,
      capitalPercentage,
      lastYearRob,
      situation,
      lastCnpj: society?.cnpj,
    });
  };

  React.useEffect(() => {
    if (!visible) {
      return;
    }
    if (!society) {
      return;
    }

    form.setFieldsValue({
      cnpj: society.cnpj,
      legalName: society.legalName,
      capitalPercentage: society.capitalPercentage,
      lastYearRob: society.lastYearRob,
      situation: society.situation,
    });
  }, [visible, society]);

  return (
    <Modal
      className="badesul-society-modal"
      visible={visible}
      title="Adicionar Empresa"
      onCancel={() => onClose()}
      isOkButtonVisible
      okButton={{
        text: 'Salvar',
        onClick: onComplete,
      }}
      destroyOnClose
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="CNPJ"
          name="cnpj"
          rules={[{ required: true, message: 'Item obrigatório' }]}
        >
          <GyramaisInput placeholder="CNPJ" />
        </Form.Item>

        <Form.Item
          label="Razão Social da Sociedade"
          name="legalName"
          rules={[{ required: true, message: 'Item obrigatório' }]}
        >
          <GyramaisInput placeholder="Razão Social da Sociedade" />
        </Form.Item>

        <Form.Item
          label="% do Capital"
          name="capitalPercentage"
          rules={[{ required: true, message: 'Item obrigatório' }]}
        >
          <GyramaisInput placeholder="% do Capital" />
        </Form.Item>

        <Form.Item
          label="ROB do último exercício"
          name="lastYearRob"
          rules={[{ required: true, message: 'Item obrigatório' }]}
        >
          <GyramaisInput placeholder="ROB do último exercício" />
        </Form.Item>

        <Form.Item
          label="Situação do Declarante"
          name="situation"
          rules={[{ required: true, message: 'Item obrigatório' }]}
        >
          <Select
            placeholder="Situação do Declarante"
            options={[
              {
                label: 'Fiador/Avalista',
                value: 'Fiador/Avalista',
              },
              {
                label: 'Interveniente',
                value: 'Interveniente',
              },
              {
                label: 'Vinculado à Sociedade como Sócio',
                value: 'Vinculado à Sociedade como Sócio',
              },
            ]}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SocietyDataRow = ({ society, onEdit, onRemove }) => (
  <Row className="badesul-society-item" key={society.cnpj}>
    <Col xs={24} sm={24} md={4} lg={10}>
      <p className="badesul-society-item-label">CNPJ</p>
      <p className="badesul-society-item-value">{society.cnpj}</p>
    </Col>
    <Col xs={24} sm={24} md={4} lg={10}>
      <p className="badesul-society-item-label">Razão Social da Sociedade</p>
      <p className="badesul-society-item-value">{society.legalName}</p>
    </Col>
    <Col xs={24} sm={24} md={4} lg={4} className="buttons">
      <Button onClick={onEdit} icon={<EditOutlined />} />
      <Button onClick={onRemove} icon={<DeleteOutlined />} color="danger" />
    </Col>
    <Col xs={24} sm={24} md={8} lg={8}>
      <p className="badesul-society-item-label">% do Capital</p>
      <p className="badesul-society-item-value">{society.capitalPercentage}</p>
    </Col>
    <Col xs={24} sm={24} md={8} lg={8}>
      <p className="badesul-society-item-label">ROB do último exercício</p>
      <p className="badesul-society-item-value">R$ {society.lastYearRob}</p>
    </Col>
    <Col xs={24} sm={24} md={8} lg={8}>
      <p className="badesul-society-item-label">Situação do Declarante</p>
      <p className="badesul-society-item-value">{society.situation}</p>
    </Col>
  </Row>
);

const SocietyForm = ({ societyData: _rawSocietyData, saveSocietyMutation }) => {
  /**
   * @type {[
   *  SocietyData[],
   *  React.Dispatch<React.SetStateAction<SocietyData[]>>
   * ]}
   */
  const [societyData, setSocietyData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const [currentSociety, setCurrentSociety] = React.useState(null);
  const [addSocietyModalVisible, setAddSocietyModalVisible] = React.useState(false);

  const onSubmit = async (society, remove = false) => {
    setIsLoading(true);
    setCurrentSociety(null);
    setAddSocietyModalVisible(false);

    const rawSocietyData = _rawSocietyData || '[]';
    let societyDataParsed = JSON.parse(rawSocietyData);

    const alreadyExists = societyDataParsed.find(
      (s) => s.cnpj === (society.lastCnpj ?? society.cnpj),
    );
    if (alreadyExists) {
      if (remove) {
        societyDataParsed = societyDataParsed.filter((s) => s.cnpj !== society.cnpj);
      } else {
        // Basicamente pra não mudar o ponteiro de referência
        alreadyExists.cnpj = society.cnpj;
        alreadyExists.legalName = society.legalName;
        alreadyExists.capitalPercentage = society.capitalPercentage;
        alreadyExists.lastYearRob = society.lastYearRob;
        alreadyExists.situation = society.situation;
      }
    } else {
      societyDataParsed = [...societyDataParsed, society];
    }

    const { mutation, baseVariables } = saveSocietyMutation;
    const variables = {
      ...baseVariables(),
      societyData: JSON.stringify(societyDataParsed),
    };

    await mutation({ variables });
    setSocietyData(societyDataParsed);
    setIsLoading(false);
  };

  React.useEffect(() => {
    const rawSocietyData = _rawSocietyData || '[]';
    const societyDataParsed = JSON.parse(rawSocietyData);

    setSocietyData(societyDataParsed);
  }, [_rawSocietyData]);

  return (
    <Row className="badesul-society-form">
      <AddSocietyModal
        society={currentSociety}
        visible={addSocietyModalVisible}
        onSubmit={onSubmit}
        onClose={() => {
          setAddSocietyModalVisible(false);
          setCurrentSociety(null);
        }}
      />

      <Col xs={24} sm={24} md={24} lg={24}>
        <Row className="badesul-society-list">
          {isLoading ? (
            <Skeleton.Button
              shape="square"
              active
              className="gyramais-input-skeleton"
              size="large"
            />
          ) : (
            societyData.map((society) => (
              <SocietyDataRow
                society={society}
                key={society.cnpj}
                onEdit={() => {
                  setCurrentSociety(society);
                  setAddSocietyModalVisible(true);
                }}
                onRemove={() => {
                  onSubmit(society, true);
                }}
              />
            ))
          )}
        </Row>
      </Col>

      <Button
        block
        loading={isLoading}
        onClick={() => {
          setAddSocietyModalVisible(true);
          setCurrentSociety(null);
        }}
      >
        Adicionar empresa
      </Button>
    </Row>
  );
};

export default SocietyForm;
